import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../api";
import {
  addHelpGuideUrl,
  deleteHelpGuideUrl,
  editHelpGuideUrl,
  getHelpGuidesUrl,
} from "../../config";
import {
  IAddUserGuidePayload,
  IEditUserGuidePayload,
  IUserGuide,
  IUserGuideState,
} from "./userGuideTypes";

const initialState: IUserGuideState = {
  userGuides: [],
  isLoading: false,
  isSubmitting: false,
  isDeleting: false,
  error: null,
};

const getErrorMessage = (error: any, fallbackMessage: string): string => {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallbackMessage
  );
};

export const getUserGuides = createAsyncThunk<
  IUserGuide[],
  void,
  { rejectValue: string }
>("userGuide/getUserGuides", async (_, { rejectWithValue }) => {
  try {
    const response = await api.get(getHelpGuidesUrl);
    return response.data?.data || [];
  } catch (error: any) {
    return rejectWithValue(
      getErrorMessage(error, "Unable to load help guides right now."),
    );
  }
});

export const addUserGuide = createAsyncThunk<
  string,
  IAddUserGuidePayload,
  { rejectValue: string }
>("userGuide/addUserGuide", async (payload, { rejectWithValue }) => {
  try {
    const formData = new FormData();

    formData.append("title", payload.title);
    formData.append("description", payload.description || "");
    formData.append("file", payload.file);

    const response = await api.post(addHelpGuideUrl, formData);

    return response.data?.message || "Help guide added successfully.";
  } catch (error: any) {
    return rejectWithValue(
      getErrorMessage(error, "Unable to add help guide. Please try again."),
    );
  }
});

export const editUserGuide = createAsyncThunk<
  string,
  IEditUserGuidePayload,
  { rejectValue: string }
>("userGuide/editUserGuide", async (payload, { rejectWithValue }) => {
  try {
    const formData = new FormData();

    formData.append("guideId", payload.guideId);
    formData.append("title", payload.title);
    formData.append("description", payload.description || "");

    if (payload.file) {
      formData.append("file", payload.file);
    }

    const response = await api.put(editHelpGuideUrl, formData);

    return response.data?.message || "Help guide updated successfully.";
  } catch (error: any) {
    return rejectWithValue(
      getErrorMessage(error, "Unable to update help guide. Please try again."),
    );
  }
});

export const deleteUserGuide = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>("userGuide/deleteUserGuide", async (guideId, { rejectWithValue }) => {
  try {
    const response = await api.delete(`${deleteHelpGuideUrl}?id=${guideId}`);

    return response.data?.message || "Help guide deleted successfully.";
  } catch (error: any) {
    return rejectWithValue(
      getErrorMessage(error, "Unable to delete help guide. Please try again."),
    );
  }
});

const userGuideSlice = createSlice({
  name: "userGuide",
  initialState,
  reducers: {
    clearUserGuideError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserGuides.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUserGuides.fulfilled, (state, action) => {
        state.isLoading = false;
        state.userGuides = action.payload;
      })
      .addCase(getUserGuides.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Unable to load help guides.";
      })

      .addCase(addUserGuide.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(addUserGuide.fulfilled, (state) => {
        state.isSubmitting = false;
      })
      .addCase(addUserGuide.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload || "Unable to add help guide.";
      })

      .addCase(editUserGuide.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(editUserGuide.fulfilled, (state) => {
        state.isSubmitting = false;
      })
      .addCase(editUserGuide.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload || "Unable to update help guide.";
      })

      .addCase(deleteUserGuide.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteUserGuide.fulfilled, (state) => {
        state.isDeleting = false;
      })
      .addCase(deleteUserGuide.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload || "Unable to delete help guide.";
      });
  },
});

export const { clearUserGuideError } = userGuideSlice.actions;

export default userGuideSlice.reducer;